import {
  JOURS_ESSAI_GRATUIT,
  NOM_FORMULE,
  PRIX_DUO_MENSUEL_CENTIMES,
  PRIX_MENSUEL_CENTIMES,
  prixEnEuros,
} from '@repere/core';
import { useEffect, useState } from 'react';
import { intervalleHebdomadaire, poserEcheance } from './cadence';
import { Bouton } from '../ui';
import { InvitationAbonnement } from './InvitationAbonnement';
import { CLE_RAPPEL } from './RappelAbonnement';
import { useVersionGratuite } from './regime';

/**
 * Ce que Solo et Duo ouvrent, à la première ouverture d'un dossier.
 *
 * **Une carte, pas une fenêtre.** Le parent vient d'entrer dans son dossier pour la
 * première fois ; lui poser une boîte de dialogue sur le chemin ferait de sa première
 * impression un péage. La carte se lit ou se saute, et l'écran reste utilisable derrière.
 *
 * **Une seule fois par dossier, et jamais deux offres à la fois.** Le repère est posé dès
 * l'affichage, et l'affichage repousse du même geste le rappel hebdomadaire
 * (`RappelAbonnement`) : sans cela, la carte d'accueil et la fenêtre du rappel pouvaient
 * tomber dans la même minute, l'une par-dessus l'autre.
 *
 * **Elle ne s'adresse qu'à un compte gratuit avéré** — `useVersionGratuite` renvoie faux
 * tant que la réponse du serveur n'est pas arrivée. Présenter une offre à quelqu'un qui l'a
 * déjà payée est la faute que ce produit ne peut pas se permettre, et rater une occasion
 * pendant une seconde n'en est pas une.
 *
 * Elle annonce les deux formules payantes sans les vendre différemment : Solo et Duo sont
 * deux formes de la même offre — Duo, c'est Solo pris pour deux —, pas un palier de base et
 * sa version supérieure. Les deux sont annoncées dans tous les cas : ce que la fenêtre dira
 * de Duo change selon que l'autre parent a rejoint ou non, mais l'offre, elle, existe dès
 * qu'il y a une invitation à envoyer.
 */
export function PresentationFormules({ dossierId }: { dossierId: string }) {
  const gratuit = useVersionGratuite();

  // Trois états et non un booléen : « on ne sait pas encore » n'est pas « à cacher ». Sans
  // cette distinction, la carte apparaissait le temps d'un rendu chez un parent qui l'avait
  // déjà vue, puis disparaissait — un clignotement à chaque ouverture de dossier.
  const [etat, setEtat] = useState<'inconnu' | 'montree' | 'retiree'>('inconnu');
  /** L'essai est-il proposé coché dans la fenêtre ? `null` quand elle est fermée. */
  const [fenetre, setFenetre] = useState<boolean | null>(null);

  useEffect(() => {
    if (!gratuit) return;
    if (memoirePresentation.dejaVue(dossierId)) {
      setEtat('retiree');
      return;
    }
    memoirePresentation.retenir(dossierId);
    // La carte consomme la sollicitation de la semaine : elle dit déjà tout ce que le
    // rappel dirait, et le dire deux fois de suite se lit comme de l'insistance.
    poserEcheance(CLE_RAPPEL, Date.now() + intervalleHebdomadaire(Math.random()));
    setEtat('montree');
  }, [gratuit, dossierId]);

  if (etat !== 'montree') return null;

  return (
    <>
      <aside className="offre-ouverture" aria-labelledby="offre-ouverture-titre">
        <h2 id="offre-ouverture-titre">Solo et Duo</h2>

        {/* Ce qui reste gratuit vient en premier, et ce n'est pas de la politesse : un
            parent qui découvre une offre au moment où il ouvre son dossier a d'abord
            besoin de savoir que la coordination avec l'autre parent ne dépend d'aucun
            paiement. Le taire ferait lire l'encart comme un compte à rebours. */}
        <p>
          Le calendrier de garde et les alertes restent gratuits avec {NOM_FORMULE.link},
          maintenant et plus tard. Prévenir l'autre parent qu'un enfant est malade ne dépend
          d'aucun abonnement.
        </p>
        <p>
          Le journal horodaté, les dépenses partagées, les documents des enfants et les
          exports pour un avocat s'ouvrent avec {NOM_FORMULE.solo} ou {NOM_FORMULE.duo}.
        </p>

        <dl className="offre-ouverture-tarifs">
          <div>
            <dt>{NOM_FORMULE.solo}</dt>
            <dd>
              <span className="montant">{prixEnEuros(PRIX_MENSUEL_CENTIMES)}</span> par mois
              <span className="discret">Pour vous.</span>
            </dd>
          </div>
          <div>
            <dt>{NOM_FORMULE.duo}</dt>
            <dd>
              <span className="montant">{prixEnEuros(PRIX_DUO_MENSUEL_CENTIMES)}</span> par
              mois
              {/* La phrase qui désamorce l'idée qu'on achète un droit SUR l'autre. Elle
                  est ici comme dans la fenêtre, et pour la même raison : c'est là que la
                  question se pose, pas un écran plus loin. */}
              <span className="discret">
                L'écriture s'ouvre aussi à l'autre parent. Il n'est pas informé de qui paie,
                et peut prendre le sien quand il veut.
              </span>
            </dd>
          </div>
        </dl>

        <div className="offre-ouverture-actions">
          <Bouton onClick={() => setFenetre(true)}>
            Essayer {JOURS_ESSAI_GRATUIT} jours gratuitement
          </Bouton>
          {/* Le choix inverse est nommé plutôt que sous-entendu, et il n'a pas l'air d'une
              porte de sortie honteuse : certains parents préfèrent payer tout de suite
              plutôt que de retenir une date de fin d'essai. */}
          <Bouton variante="secondaire" onClick={() => setFenetre(false)}>
            S'abonner sans essai
          </Bouton>
          <Bouton variante="lien" onClick={() => setEtat('retiree')}>
            Plus tard
          </Bouton>
        </div>
      </aside>

      {fenetre === null ? null : (
        <InvitationAbonnement
          motif="ouverture"
          essaiParDefaut={fenetre}
          onFermer={() => {
            setFenetre(null);
            // L'offre a été vue en entier : la garder derrière la fenêtre qu'on vient de
            // fermer la ferait relire une seconde fois, en plus petit.
            setEtat('retiree');
          }}
        />
      )}
    </>
  );
}

/**
 * La clé garde son ancien nom, alors que l'offre a changé de vocabulaire.
 *
 * La renommer remettrait le compteur à zéro dans le navigateur de chaque parent : la carte
 * « une seule fois dans la vie du dossier » repasserait une fois de plus chez tous ceux qui
 * l'ont déjà vue, pour un renommage qu'eux ne voient pas. Le nom d'une clé de stockage n'a
 * pas de lecteur.
 */
const CLE_MEMOIRE = 'repere.version-parent.presentee';

/**
 * Se souvient que la carte des formules a été présentée, dossier par dossier.
 *
 * Par dossier et non par compte : un parent qui ouvre un second dossier, des années plus
 * tard, entre dans une nouvelle histoire — et la version gratuite d'un dossier partagé ne
 * se découvre pas au même moment que celle d'un dossier créé seul.
 *
 * `localStorage` peut être indisponible — navigation privée, stockage bloqué. C'est traité
 * comme un confort, mais le défaut penche ici vers **ne pas montrer** : `dejaVue` répond
 * vrai quand la lecture échoue. Sans mémoire, une carte commerciale reparaîtrait à chaque
 * ouverture de dossier, ce qui est bien pire que de la rater une fois.
 */
export const memoirePresentation = {
  dejaVue(dossierId: string): boolean {
    try {
      // Un stockage **absent** compte comme « déjà vue », au même titre qu'un stockage qui
      // jette : le `?.` optionnel aurait rendu `undefined`, donc « jamais vue », et la
      // carte serait revenue à chaque ouverture de dossier — exactement ce qu'on évite.
      const stockage = globalThis.localStorage;
      if (!stockage) return true;
      return stockage.getItem(`${CLE_MEMOIRE}.${dossierId}`) === 'oui';
    } catch {
      return true;
    }
  },
  retenir(dossierId: string): void {
    try {
      globalThis.localStorage?.setItem(`${CLE_MEMOIRE}.${dossierId}`, 'oui');
    } catch {
      /* stockage refusé : `dejaVue` répondra vrai, et la carte ne reviendra pas */
    }
  },
};
